'use client'

import { useEffect, useState } from 'react'
import { Logo } from '@/components/brand/logo'
import { useIsMobile } from '@/hooks/use-mobile'

const ROTATING_WORDS = ['Prescribed.', 'Diagnosed.', 'Optimized.', 'Protected.', 'Simplified.']

export function BrandPanel() {
  const isMobile = useIsMobile()
  const [wordIndex, setWordIndex] = useState(0)
  const [swapping, setSwapping] = useState(false)

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
    const interval = setInterval(() => {
      setSwapping(true)
      setTimeout(() => {
        setWordIndex((i) => (i + 1) % ROTATING_WORDS.length)
        setSwapping(false)
      }, 350)
    }, 2000)
    return () => clearInterval(interval)
  }, [])

  return (
    <aside
      className="relative sticky top-4 hidden min-w-0 flex-1 basis-0 self-start overflow-hidden rounded-2xl md:flex"
      style={{
        height: 'calc(100vh - 2rem)',
        background:
          'radial-gradient(circle at 15% 10%, #2b3499 0%, transparent 45%), linear-gradient(160deg, #1e2480 0%, #131740 55%, #080a18 100%)',
      }}
    >
      <div
        className="pointer-events-none absolute z-0 rounded-full"
        style={{
          width: 520,
          height: 520,
          top: -160,
          left: -160,
          background: 'radial-gradient(circle, rgba(68,76,231,0.18) 0%, transparent 65%)',
        }}
      />
      <div className="absolute inset-0 z-0">
        <video
          className="absolute inset-0 h-full w-full object-cover opacity-75 mix-blend-screen"
          autoPlay
          muted
          loop
          playsInline
        >
          {/* This panel itself is hidden below `md:` via its wrapper's `hidden md:flex`, but the
              element still mounts — gate the source so it only fetches on an actual desktop
              viewport, avoiding a duplicate fetch alongside SplashView's own mobile video. */}
          {!isMobile && <source src="/video/login-brand-bg.mp4" type="video/mp4" />}
        </video>
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(8,10,24,0.35) 0%, rgba(8,10,24,0.25) 45%, rgba(8,10,24,0.92) 100%), linear-gradient(135deg, rgba(68,76,231,0.35) 0%, rgba(30,36,128,0.2) 60%)',
          }}
        />
      </div>

      <div className="relative z-10 flex h-full flex-col p-10">
        <div className="mb-6 flex items-center gap-2.5">
          <Logo />
          <span className="text-lg font-bold tracking-tight text-white">Nivex</span>
        </div>
        <div className="mt-auto">
          <span
            className="mb-5 inline-flex w-fit items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium text-white"
            style={{
              backgroundColor: 'rgba(255,255,255,0.14)',
              border: '1px solid rgba(255,255,255,0.28)',
              backdropFilter: 'blur(10px)',
            }}
          >
            <span className="size-1.5 shrink-0 rounded-full bg-blue-600" />
            India&apos;s First Prescription-Based Wealth Platform
          </span>
          <h1 className="mb-5 text-[clamp(36px,3.5vw,52px)] font-medium leading-[1.1] tracking-[-0.03em] text-white">
            Your
            <br />
            Wealth.
            <br />
            <em
              className="inline-block font-medium not-italic text-[#A5B4FC] transition-all duration-[350ms]"
              style={
                swapping
                  ? { opacity: 0, transform: 'translateY(6px)' }
                  : { opacity: 1, transform: 'translateY(0)' }
              }
            >
              {ROTATING_WORDS[wordIndex]}
            </em>
          </h1>
          <p className="max-w-[300px] text-[15px] leading-relaxed text-white/45">
            We diagnose your financial health first, then build a personalised wealth prescription —
            not the other way around.
          </p>
        </div>
      </div>
    </aside>
  )
}
